/**
 * 낭만작명 결과 어댑터.
 *
 * `GET /fast/base-service`(analysisTypeId=47) 응답을 FE 결과 컴포넌트
 * (ContentsRecommend01 / ContentsAnalyze01)가 기대하는 구조로 변환한다.
 *
 * 백엔드 응답 특성 (검사로 확인):
 * - 오행은 한자(木/火/土/金/水)로 옴 → ohaengPalette는 한글 키라 변환 필요.
 * - 최상위 용신 오행 키가 모드마다 다름: `용신오행`(사람) / `사용오행`(반려·상호·닉네임).
 * - 추천 항목 이름 키가 모드마다 다름: `한글이름` / `한자이름` / `이름` / `입력`.
 * - `규칙별해석`은 일부 규칙만 옴 → 나머지는 분석객체(조후/입묘/공망/용신분석)로 폴백 생성.
 * - 등급(grade)·규칙별 등급(최상/상/중)·규칙별 title 은 응답에 없음 → FE 산출.
 */
import { getNamingRulePalette } from '@/utils/namingRulePalette';

/** 오행 한자 → 한글 (ohaengPalette/getOhaeng 가 한글 키를 사용) */
const OHAENG_HANJA_TO_KOR = { 木: '목', 火: '화', 土: '토', 金: '금', 水: '수' };
export const toKorOhaeng = (h) => OHAENG_HANJA_TO_KOR[h] || h || '';

/** "木, 火, 土" → ["목","화","토"] */
const splitOhaengStr = (s) =>
  (s || '')
    .split(/[,\s]+/)
    .filter(Boolean)
    .map(toKorOhaeng);

/**
 * 종합점수 → 등급(S/A/B/C/D).
 * TODO: 실측 점수대가 낮아(추천 최고 ~83, 분석 ~30) 임시 구간. 기획/BE 확정 시 교체.
 */
export const scoreToGrade = (score) => {
  const s = Number(score) || 0;
  if (s >= 90) return 'S';
  if (s >= 80) return 'A';
  if (s >= 70) return 'B';
  if (s >= 60) return 'C';
  return 'D';
};

/** 규칙별 획득/배점 비율 → 등급 텍스트+색 (최상/상/중/주의) */
const ratioToGrade = (value, max) => {
  if (!max) return { text: '', color: '#757575' };
  const r = (Number(value) || 0) / max;
  if (r >= 0.9) return { text: '최상', color: '#53b82c' };
  if (r >= 0.7) return { text: '상', color: '#376ac9' };
  if (r >= 0.4) return { text: '중', color: '#f3ae11' };
  return { text: '주의', color: '#e65555' };
};

/** 응답 규칙등급(하/중/상/최상) → 색 (ratioToGrade 색과 정합) */
const GRADE_COLORS = { 최상: '#53b82c', 상: '#376ac9', 중: '#f3ae11', 하: '#e65555' };
/** 규칙별 등급: 응답 `규칙등급` 우선, 없으면 점수÷배점 비율로 산출(폴백) */
const ruleGrade = (item, rule, value, max) => {
  const g = item?.규칙등급?.[rule];
  return g ? { text: g, color: GRADE_COLORS[g] || '#757575' } : ratioToGrade(value, max);
};

/** 이름 키 정규화 (모드마다 한글이름/한자이름/이름/입력) */
const pickName = (item) =>
  item?.한글이름 ?? item?.한자이름 ?? item?.이름 ?? item?.상호명 ?? item?.입력 ?? '';

/** 최상위 용신 오행 키 정규화 (용신오행/사용오행) → 한글 배열 */
export const pickYongsinOhaeng = (data) =>
  splitOhaengStr(data?.용신오행 ?? data?.사용오행 ?? '');

/** 삼원오행 배치명(flowName) — 삼원오행분석.패턴 우선, 없으면 규칙별해석.삼원오행의 【...】 */
const pickFlowName = (item) => {
  const pat = item?.삼원오행분석?.패턴;
  if (pat && pat !== '불완전') return pat;
  const txt = item?.규칙별해석?.삼원오행 || '';
  const m = txt.match(/【(.+?)】/);
  if (m) return m[1];
  return pat || '';
};

/** 용신 일치 여부 — 일치 분류에 용신/희신/한신이 있으면 true */
const isMatched = (item) =>
  !!item?.용신분석?.일치?.some((m) => ['용신', '희신', '한신'].includes(m?.분류));

/**
 * 규칙별 해석 텍스트 — 응답의 `규칙별해석`에 온 것만 사용한다.
 * 분석객체(조후/입묘/공망 등)로 문구를 합성하지 않는다(데이터에 없으면 표시도 안 함).
 */
const buildRuleDesc = (rule, item) => {
  const raw = item?.규칙별해석?.[rule] || '';
  // "입묘 4/4점(최상) — 설명"처럼 헤더(점수·등급)와 중복되는 앞부분을 제거하고 설명만 남긴다
  return raw.replace(/^.*?[\d.]+\/[\d.]+점\([^)]*\)\s*[—–-]+\s*/, '');
};

/** 적용규칙.규칙목록 + 점수상세 → scoreItems (규칙 수 동적: 한글 5 / 한자 8 등) */
const buildScoreItems = (item, fallbackRules = []) => {
  // 추천 항목에 적용규칙이 없고 전체데이터에만 있는 모드(반려·상호·닉네임 등)가 있어 폴백을 받는다.
  const rules = item?.적용규칙?.규칙목록 || fallbackRules || [];
  const detail = item?.점수상세 || {};
  return rules.map((r) => {
    const value = detail[r.규칙] ?? 0;
    const max = r.배점;
    const g = ruleGrade(item, r.규칙, value, max);
    const pal = getNamingRulePalette(r.규칙);
    return {
      key: r.규칙,
      label: r.규칙,
      value,
      max,
      headerBg: pal.headerBg,
      contentBg: pal.contentBg,
      gradeText: g.text,
      gradeColor: g.color,
      title: '',
      desc: buildRuleDesc(r.규칙, item),
    };
  });
};

/** 낭만 해석 lines (전체해석/플러스해석 중복 제거) */
const buildInterpretLines = (item) =>
  [...new Set([item?.전체해석, item?.플러스해석].filter(Boolean))];

/**
 * 적용규칙.규칙목록 → 추천기준보기 "적용 규칙" rules.
 * 규칙·배점·설명은 응답 그대로 쓰고, 색만 규칙명으로 팔레트 lookup.
 * (모드별 규칙 수·종류가 응답에 따라 동적: 한글 5 / 한자 8 / 상호 7 …)
 */
export const adaptCriteriaRules = (full) => {
  // 적용규칙이 전체데이터 레벨(상호추천)에 없고 추천목록 항목(한글 이름 추천 등)에만 있는 모드 대응
  const rules = full?.적용규칙?.규칙목록 || full?.추천목록?.[0]?.적용규칙?.규칙목록 || [];
  if (rules.length) {
    return rules.map((r) => {
      const pal = getNamingRulePalette(r.규칙);
      return { label: r.규칙, point: r.배점, desc: r.설명, color: pal.color, bg: pal.bg };
    });
  }
  // 아호 등 적용규칙이 없는 신포맷 — 첫 항목 점수상세 키 + 임시 배점표로 구성(설명 없음)
  const first = full?.추천목록?.[0];
  if (first && isAhoShape(first)) {
    return Object.keys(first.점수상세 || {}).map((rule) => {
      const pal = getNamingRulePalette(AHO_PALETTE_KEY[rule] || rule);
      return { label: rule, point: AHO_RULE_MAX[rule] ?? '', desc: '', color: pal.color, bg: pal.bg };
    });
  }
  // 닉네임 등 claude 신포맷 — 점수상세 키 + 규칙별해석에서 배점·설명 추출
  if (first && isClaudeNameShape(first)) {
    return Object.keys(first.점수상세 || {}).map((rule) => {
      const pal = getNamingRulePalette(AHO_PALETTE_KEY[rule] || rule);
      return {
        label: rule,
        point: pickClaudeRuleMax(first, rule) ?? '',
        desc: buildRuleDesc(rule, first),
        color: pal.color,
        bg: pal.bg,
      };
    });
  }
  return [];
};

/**
 * 추천기준보기 "작명 기준"의 응답 기반 행(입력값 뒤에 붙는 꼬리 행).
 * - 용신오행: 사주정보.용신오행(사주 실제 용신, badges)
 * - 채점 기준: 채점기준 + 규칙 수 + 총점
 * (사용자 입력값 행은 FE에서 detailInput으로 생성해 이 앞에 붙인다.)
 */
export const adaptCriteriaTailRows = (full) => {
  const rows = [];
  const yo = splitOhaengStr(full?.사주정보?.용신오행); // 사주 실제 용신(단일)만 — 작명에 쓴 오행(사용오행) 아님
  if (yo.length) rows.push({ label: '용신오행', badges: yo });
  // 채점기준·적용규칙도 전체데이터 레벨에 없으면 추천목록 첫 항목에서 (한글 이름 추천 등)
  const 적용 = full?.적용규칙 ?? full?.추천목록?.[0]?.적용규칙;
  const 채점 = full?.채점기준 ?? full?.추천목록?.[0]?.채점기준;
  const cnt = 적용?.규칙목록?.length;
  if (채점 && cnt) {
    const 총점 = 적용?.총점 ?? 100;
    rows.push({ label: '채점 기준', value: `${채점} · ${cnt}개 규칙 ${총점}점 만점` });
  }
  return rows;
};

/** 추천 1건 → ContentsRecommend01 recommendList 항목 */
export const adaptRecommendItem = (item, fallbackRules = []) => {
  const ohaeng = (item?.용신분석?.전체오행 || []).map(toKorOhaeng);
  const score = item?.종합점수 ?? 0;
  const scoreItems = buildScoreItems(item, fallbackRules);

  const categories = [];
  const interpretLines = buildInterpretLines(item);
  if (interpretLines.length) {
    categories.push({
      key: 'interpret',
      label: '낭만 해석',
      icon: 'essay',
      headerBg: '#f7f3ee',
      contentBg: 'rgba(247, 243, 238, 0.4)',
      lines: interpretLines,
    });
  }
  for (const s of scoreItems) {
    // desc(규칙별해석)가 없어도 점수·등급 카드는 표시 — 설명 줄만 컴포넌트에서 생략
    categories.push({
      key: s.key,
      label: s.label,
      headerBg: s.headerBg,
      contentBg: s.contentBg,
      value: s.value,
      max: s.max,
      grade: s.gradeText,
      gradeColor: s.gradeColor,
      title: '',
      desc: s.desc,
    });
  }

  return {
    name: pickName(item),
    score,
    grade: item?.종합등급 || scoreToGrade(score),
    ohaeng,
    matched: isMatched(item),
    fromOhaeng: ohaeng[0] || '',
    toOhaeng: ohaeng[ohaeng.length - 1] || ohaeng[0] || '',
    scoreItems: scoreItems.map((s) => ({ key: s.key, label: s.label, value: s.value, max: s.max })),
    categories,
    의미: item?.의미 || '',
    추천이유: item?.추천이유 || '',
  };
};

/* ────────────── 아호추천 전용(신포맷) ──────────────
 * 아호추천만 응답 스키마가 다른 모드와 다르다(chars/hanja/hangul/meaning/reason/점수상세/플러스해석).
 * isAhoShape로 감지해 adaptAhoItem으로 분기하므로, 추후 BE가 표준 포맷(적용규칙/용신분석/
 * 규칙별해석/한글이름)으로 통일하면 isAhoShape가 false가 되어 자동으로 표준 경로를 타고,
 * 이 블록(상수 2개 + 함수 + 분기 한 줄)만 제거하면 된다. 다른 모드 경로는 건드리지 않는다.
 */
/** 아호 규칙별 만점 — 응답에 배점이 없어 임시 하드코딩(BE 확인 필요). 점수요약 분포로 추정. */
const AHO_RULE_MAX = { 용신: 40, 조후: 20, 입묘: 10, 공망: 10, 한자범주: 10, 음양균형: 10 };
/** 규칙명 → 팔레트 키(팔레트는 공백 포함 표기) */
const AHO_PALETTE_KEY = { 한자범주: '한자 범주', 음양균형: '음양 균형' };

/**
 * 아호추천 응답이 신포맷인지 (글자별 chars / 한자 hanja 보유).
 * 단, 아호는 다른 모드가 쓰는 어떤 이름 필드도 갖지 않는다는 점으로 구분한다:
 * - 표준 한글 이름 필드(`한글이름/한자이름/이름/상호명/입력`, pickName 기준) — 반려식물·상호·일반작명
 * - claude 신포맷의 영문 `name` 필드 — 닉네임추천
 * 위 이름 필드가 하나라도 있으면 chars가 있어도 아호가 아니므로, 각자 표준/claude 경로로 보낸다.
 */
export const isAhoShape = (item) =>
  !!(item?.chars || item?.hanja) && !pickName(item) && item?.name == null;

/** 아호추천 1건 → ContentsRecommend01 recommendList 항목 */
export const adaptAhoItem = (item) => {
  const ohaeng = (item?.chars || []).map((c) => toKorOhaeng(c?.ohaeng)).filter(Boolean);
  const score = item?.종합점수 ?? 0;
  const detail = item?.점수상세 || {};
  const scoreItems = Object.keys(detail).map((rule) => ({
    key: rule,
    label: rule,
    value: detail[rule],
    max: AHO_RULE_MAX[rule] ?? null,
  }));

  const categories = [];
  const interpretLines = [...new Set([item?.플러스해석].filter(Boolean))];
  if (interpretLines.length) {
    categories.push({
      key: 'interpret',
      label: '낭만 해석',
      icon: 'essay',
      headerBg: '#f7f3ee',
      contentBg: 'rgba(247, 243, 238, 0.4)',
      lines: interpretLines,
    });
  }
  for (const s of scoreItems) {
    const g = ruleGrade(item, s.key, s.value, s.max);
    const pal = getNamingRulePalette(AHO_PALETTE_KEY[s.key] || s.key);
    categories.push({
      key: s.key,
      label: s.label,
      headerBg: pal.headerBg,
      contentBg: pal.contentBg,
      value: s.value,
      max: s.max,
      grade: g.text,
      gradeColor: g.color,
      title: '',
      desc: buildRuleDesc(s.key, item), // 응답 규칙별해석에서 점수·등급 머리말 제거 후 표시
    });
  }

  return {
    name: item?.hanja || item?.hangul || '',
    // 한자 이름일 때만 한글 음을 부기 (한글 추천이면 name이 이미 한글이라 중복 표기 방지)
    hangul: item?.hanja && item?.hangul && item.hanja !== item.hangul ? item.hangul : '',
    score,
    grade: item?.종합등급 || scoreToGrade(score),
    ohaeng,
    matched: (detail.용신 ?? 0) >= (AHO_RULE_MAX.용신 || 40),
    fromOhaeng: ohaeng[0] || '',
    toOhaeng: ohaeng[ohaeng.length - 1] || ohaeng[0] || '',
    scoreItems,
    categories,
    의미: item?.meaning || '',
    추천이유: item?.reason || '',
  };
};

/* ────────────── 닉네임 등 claude 자유추천(신포맷) ──────────────
 * 응답이 name/final_score/점수상세/규칙등급/규칙별해석/플러스해석 형태로 오는 모드.
 * isAhoShape(chars/hanja)와 달리 글자별 오행이 없어 yongshin_note에서 오행을 추출한다.
 * BE가 표준 포맷(한글이름/종합점수/적용규칙/용신분석)으로 통일하면 isClaudeNameShape가
 * false가 되어 자동으로 표준 경로를 타고, 이 블록만 제거하면 된다.
 */
/** claude 신포맷 추천 감지 (name + 점수상세 + 규칙등급/규칙별해석 보유) */
export const isClaudeNameShape = (item) =>
  !!(item?.name && item?.점수상세 && (item?.규칙등급 || item?.규칙별해석));

/** 규칙별해석 "용신 40/40점(최상) — ..." 에서 배점(max) 추출 */
const pickClaudeRuleMax = (item, rule) => {
  const m = (item?.규칙별해석?.[rule] || '').match(/\/\s*([\d.]+)\s*점/);
  return m ? Number(m[1]) : null;
};

/** claude 신포맷 추천 1건 → ContentsRecommend01 recommendList 항목 */
export const adaptClaudeItem = (item) => {
  const detail = item?.점수상세 || {};
  const score = item?.final_score ?? item?.종합점수 ?? 0;
  // claude 포맷엔 글자별 오행이 없어 yongshin_note("목(ㄱ)+화(ㄴ)…")에서 추출
  const ohaeng = [...(item?.yongshin_note || '').matchAll(/([목화토금수])\s*\(/g)].map((m) => m[1]);
  const scoreItems = Object.keys(detail).map((rule) => ({
    key: rule,
    label: rule,
    value: detail[rule],
    max: pickClaudeRuleMax(item, rule),
  }));

  const categories = [];
  const interpretLines = [...new Set([item?.전체해석, item?.플러스해석].filter(Boolean))];
  if (interpretLines.length) {
    categories.push({
      key: 'interpret',
      label: '낭만 해석',
      icon: 'essay',
      headerBg: '#f7f3ee',
      contentBg: 'rgba(247, 243, 238, 0.4)',
      lines: interpretLines,
    });
  }
  for (const s of scoreItems) {
    const g = ruleGrade(item, s.key, s.value, s.max);
    const pal = getNamingRulePalette(AHO_PALETTE_KEY[s.key] || s.key);
    categories.push({
      key: s.key,
      label: s.label,
      headerBg: pal.headerBg,
      contentBg: pal.contentBg,
      value: s.value,
      max: s.max,
      grade: g.text,
      gradeColor: g.color,
      title: '',
      desc: buildRuleDesc(s.key, item),
    });
  }

  return {
    name: item?.name || '',
    score,
    grade: item?.종합등급 || scoreToGrade(score),
    ohaeng,
    matched: item?.규칙등급?.용신 === '최상',
    fromOhaeng: ohaeng[0] || '',
    toOhaeng: ohaeng[ohaeng.length - 1] || ohaeng[0] || '',
    scoreItems,
    categories,
    의미: item?.meaning || '',
    추천이유: item?.yongshin_note || '',
  };
};

/** 분석 전체데이터 → ContentsAnalyze01 analysis */
export const adaptAnalysis = (full) => {
  const score = full?.종합점수 ?? 0;
  return {
    score,
    grade: full?.종합등급 || scoreToGrade(score),
    ohaengFlow: (full?.용신분석?.전체오행 || []).map(toKorOhaeng),
    flowName: pickFlowName(full),
    scoreItems: buildScoreItems(full),
    summary: [full?.전체해석].filter(Boolean),
    interpret: [full?.플러스해석].filter(Boolean),
    // 명리총평: 표시할 데이터 형식이 미확정이라 임시로 비워 숨긴다(ContentsAnalyze01의 v-if로 미표시).
    // BE 데이터 형식 확정 시 여기서 채우면 자동 노출.
    gameSummary: [],
  };
};

/* ────────────── 기준보기 팝업 사주정보 ────────────── */
/** 명식 키 → 기둥 라벨 (시/일/월/년) */
const MYEONGSIK_POS = [['시주', '시'], ['일주', '일'], ['월주', '월'], ['년주', '년']];
/** 배열/문자열 → " · " 결합 ("술","해" → "술 · 해", "목화" 문자열은 그대로) */
const joinDot = (v) => (Array.isArray(v) ? v.filter(Boolean).join(' · ') : v || '');

/**
 * 응답 `사주정보`(또는 `전체데이터.사주정보`) → 기준보기 팝업용 { myeongsik, tags }.
 * - myeongsik: 명식(시/일/월/년) 한글 간지 문자열 배열. 색·한자를 입힌 pillars 변환은
 *   fiveMixin이 필요해 컴포넌트(DetailPageNaming)에서 수행한다(어댑터는 순수 변환만 유지).
 * - tags: 용신/희신/한신/기신/구신·조후·공망·격국 명리 요소 라벨(텍스트 chip). 빈 값은 제외.
 */
export const adaptSajuInfo = (saju) => {
  if (!saju) return { myeongsik: [], tags: [] };
  const ms = saju.명식 || {};
  const myeongsik = MYEONGSIK_POS
    .map(([key, label]) => ({ label, ganji: ms[key] || '' }))
    .filter((p) => p.ganji);

  const tags = [];
  const push = (label, val) => { if (val) tags.push({ label: `${label}: ${val}` }); };
  push('용신', saju.용신오행);
  push('희신', saju.희신오행);
  push('한신', saju.한신오행);
  push('기신', saju.기신오행);
  push('구신', saju.구신오행);
  push('조후', joinDot(saju.조후용신));
  push('공망', joinDot(saju.공망));
  push('격국', saju.격국);

  return { myeongsik, tags };
};

/**
 * 응답 data 판별 + 변환.
 * @returns {{kind:'recommend', list, yongsinOhaeng}
 *          | {kind:'analyze', analysis, name, yongsinOhaeng}
 *          | {kind:'error', message}}
 */
export const adaptNamingResult = (data) => {
  if (!data) return { kind: 'error', message: '결과가 없습니다.' };
  if (data.오류) return { kind: 'error', message: data.오류 };

  const full = data.전체데이터;
  const sajuInfo = adaptSajuInfo(full?.사주정보 || data?.사주정보);

  // 추천: 전체데이터.추천목록(구조화 배열) 존재
  if (full?.추천목록?.length) {
    // 항목에 적용규칙이 없는 모드(반려 등) 대비 — 전체데이터의 규칙목록을 폴백으로 주입
    const fallbackRules = full?.적용규칙?.규칙목록 || [];
    return {
      kind: 'recommend',
      yongsinOhaeng: pickYongsinOhaeng(full),
      criteriaRules: adaptCriteriaRules(full),
      criteriaTailRows: adaptCriteriaTailRows(full),
      sajuInfo,
      list: full.추천목록.map((it) =>
        isAhoShape(it)
          ? adaptAhoItem(it)
          : isClaudeNameShape(it)
            ? adaptClaudeItem(it)
            : adaptRecommendItem(it, fallbackRules)),
    };
  }

  // 분석: 전체데이터 + 종합점수
  if (full && full.종합점수 != null) {
    return {
      kind: 'analyze',
      yongsinOhaeng: pickYongsinOhaeng(full),
      analysis: adaptAnalysis(full),
      name: pickName(full),
      sajuInfo,
    };
  }

  // 추천목록_요약만 있고 추천목록 객체배열이 없는 미완성 응답 등
  return { kind: 'error', message: '아직 준비되지 않은 결과예요. 잠시 후 다시 시도해주세요.' };
};
